import { getFileName, getFileNameWithExtension } from "../../../utils/file/filePath";
import { BucketObjectType } from "../types/BucketObjectType";
import { LegacyBucketObjectItem } from "./LegacyBucketObjectItem";

export type LegacyBucketObject = LegacyBucketFolder | LegacyBucketFile;

export interface LegacyBucketFolder extends LegacyBucketObjectBase {
    type: BucketObjectType.Folder;
    content: LegacyBucketObject[];
}

export interface LegacyBucketFile extends LegacyBucketObjectBase {
    type: BucketObjectType.File;
    sizeInBytes: number;
    lastModified: string;
    isInactive?: boolean;
}

interface LegacyBucketObjectBase {
    id: string;
    type: BucketObjectType;
    path: string;
    name: string;
    isSelectable?: boolean | string;
}

export const isLegacyObjectFolder = (object: LegacyBucketObject): object is LegacyBucketFolder =>
    object.type === BucketObjectType.Folder;
export const isLegacyObjectFile = (object: LegacyBucketObject): object is LegacyBucketFile =>
    object.type === BucketObjectType.File;

export const filterBucketContentByPaths = (
    content: LegacyBucketObject[],
    excludedFilePaths: string[]
): LegacyBucketObject[] => {
    if (!excludedFilePaths.length) {
        return content;
    }

    return content.reduce<LegacyBucketObject[]>((newContent, item) => {
        if (item.type === BucketObjectType.File && excludedFilePaths.includes(item.path)) {
            return newContent;
        }

        if (item.type === BucketObjectType.Folder) {
            return [...newContent, { ...item, content: filterBucketContentByPaths(item.content, excludedFilePaths) }];
        }

        return [...newContent, item];
    }, []);
};

export const mapItemToLegacyBucketObject = (object: LegacyBucketObjectItem): LegacyBucketObject => {
    switch (object.type) {
        case BucketObjectType.File: {
            return {
                id: getFileNameWithExtension(object.path),
                type: BucketObjectType.File,
                path: object.path,
                name: getFileNameWithExtension(object.path),
                sizeInBytes: object.sizeInBytes,
                lastModified: object.lastModified,
                isSelectable: object.isSelectable,
                isInactive: object.isInactive
            };
        }

        case BucketObjectType.Folder: {
            return {
                id: getFileName(object.path),
                type: BucketObjectType.Folder,
                path: object.path,
                name: getFileName(object.path),
                content: object.content.map(mapItemToLegacyBucketObject),
                isSelectable: object.isSelectable
            };
        }
    }
};
